// AI Max Brand Exclusions — judgment row, Negatives section.
// LLM-drafted verdict (rendered above the collapse boundary by FindingCard) on whether
// generic/conquesting AI-Max campaigns are restricting branded traffic. This renderer supplies
// the supporting "AI Max campaigns Claude saw" table — each campaign with its campaign-level
// brand-list controls (inclusion vs. exclusion) — for verification.

import type { EvidenceProps } from './registry';

interface BrandControl {
  kind: 'inclusion' | 'exclusion';
  brandListId: string | null;
  brandListName: string;
  memberCount: number;
}

interface CampaignRow {
  id?: string;
  name: string;
  controls: BrandControl[];
}

interface BrandListRow {
  id: string;
  name: string;
  memberCount: number;
}

interface AiMaxBrandShape {
  totalSearchCampaigns: number;
  aiMaxCampaignCount: number;
  aiMaxWithExclusionCount: number;
  aiMaxWithoutAnyControlCount: number;
  brandListsOnAccount: BrandListRow[];
  campaigns: CampaignRow[];
}

export const AiMaxBrandExclusionsEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as AiMaxBrandShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          AI Max coverage
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.aiMaxCampaignCount} of {e.totalSearchCampaigns} Search campaigns
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.aiMaxCampaignCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>AI Max on</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {e.aiMaxWithExclusionCount.toLocaleString()}
            </div>
            <div className='audience-coverage__lbl'>With brand exclusion</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {e.aiMaxWithoutAnyControlCount.toLocaleString()}
            </div>
            <div className='audience-coverage__lbl'>No brand control</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>
              {e.brandListsOnAccount.length.toLocaleString()}
            </div>
            <div className='audience-coverage__lbl'>Brand lists on account</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          AI Max campaigns Claude saw
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.campaigns.length} campaigns
          </span>
        </h4>
        <table className='evidence__table evidence__table--ai-max-brand'>
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Brand-list control</th>
            </tr>
          </thead>
          <tbody>
            {e.campaigns.map(c => (
              <tr key={c.id ?? c.name}>
                <td>{c.name}</td>
                <td>
                  {c.controls.length === 0 ? (
                    <span className='evidence__inline-note'>No brand-list control</span>
                  ) : (
                    <span className='evidence__modifier-list'>
                      {c.controls.map((ctrl, i) => (
                        <span
                          key={`${ctrl.brandListId ?? ctrl.brandListName}-${i}`}
                          className={`evidence__brand-chip evidence__brand-chip--${ctrl.kind}`}
                          title={`${ctrl.memberCount} brand${ctrl.memberCount === 1 ? '' : 's'}`}
                        >
                          {ctrl.kind === 'exclusion' ? 'Excludes' : 'Includes'} {ctrl.brandListName}
                        </span>
                      ))}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        AI Max expands a Search campaign's matching beyond its keywords, so branded queries can
        reach campaigns that never targeted them. A brand <strong>exclusion</strong> (a BRANDS list
        applied negatively) keeps those queries out — typically wanted on generic/non-brand
        campaigns so they don't absorb high-intent brand traffic. A brand <strong>inclusion</strong>{' '}
        restricts a campaign to only that brand's queries — appropriate on brand campaigns. Note:
        brand exclusions reduce volume, so they're recommended where negative keywords alone aren't
        filtering branded queries.
      </p>
    </div>
  );
};
